/** Room details with the day's schedule, current state and the room's rules. */
import { useQuery } from "@tanstack/react-query";

import { Dialog } from "@/components/Dialog";
import { Icon } from "@/components/Icon";
import { ErrorState, SkeletonRows } from "@/components/States";
import { StatusPill } from "@/components/StatusPill";
import { api } from "@/lib/api";
import { formatTime } from "@/lib/format";
import type { Policy, RoomDisplay, SearchRoom } from "@/lib/types";

/** The detail endpoint returns the room plus a fuller day schedule than the
 *  search results carry, so it is typed on its own rather than extending
 *  SearchRoom. */
interface RoomDetailPayload extends Omit<SearchRoom, "schedule"> {
  display: RoomDisplay;
  policy: Policy | null;
  schedule: { start_at: string; end_at: string; subject: string; own: boolean; status: string }[];
}

const TONE: Record<string, "available" | "soon" | "occupied" | "offline"> = {
  available: "available",
  starting_soon: "available",
  free_soon: "soon",
  occupied: "occupied",
  check_in_required: "occupied",
  no_show_warning: "occupied",
  maintenance: "offline",
  offline: "offline",
  not_configured: "offline",
};

export function RoomDetail({
  roomId,
  date,
  onClose,
  onBook,
}: {
  roomId: string;
  date: string;
  onClose: () => void;
  onBook: (room: SearchRoom) => void;
}) {
  const query = useQuery({
    queryKey: ["room-detail", roomId, date],
    queryFn: () =>
      api.get<RoomDetailPayload>(`/portal/rooms/${roomId}`, {
        day: new Date(`${date}T09:00:00`).toISOString(),
      }),
  });

  const room = query.data;

  return (
    <Dialog
      open
      width="wide"
      title={room?.name ?? "Room"}
      description={
        room
          ? `${room.capacity} seats · ${room.location_name ?? ""}${room.floor ? ` · ${room.floor}` : ""}`
          : undefined
      }
      onClose={onClose}
      footer={
        room ? (
          <>
            <button className="btn btn--secondary" onClick={onClose}>Close</button>
            <button className="btn btn--primary"
              onClick={() => onBook({ ...room, schedule: [] } as SearchRoom)}>
              Book this room
            </button>
          </>
        ) : undefined
      }
    >
      {query.isPending ? (
        <SkeletonRows rows={6} height={44} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} compact />
      ) : room ? (
        <div className="stack" style={{ gap: "var(--space-5)" }}>
          <div className="row" style={{ gap: "var(--space-3)", flexWrap: "wrap" }}>
            <StatusPill tone={TONE[room.display.state] ?? "offline"}>
              {room.display.label}
            </StatusPill>
            <span className="t-body ink-secondary">{room.display.subline}</span>
          </div>

          {room.description && <p className="t-body ink-secondary">{room.description}</p>}

          {room.amenities.length > 0 && (
            <div className="stack" style={{ gap: "var(--space-2)" }}>
              <span className="t-label">In this room</span>
              <div className="chips">
                {room.amenities.map((amenity) => (
                  <span className="amenity" key={amenity}>
                    <Icon name="check" size={13} />
                    {amenity}
                  </span>
                ))}
                {room.accessibility.map((item) => (
                  <span className="amenity" key={item}>
                    <Icon name="accessible" size={13} />
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="stack" style={{ gap: "var(--space-2)" }}>
            <span className="t-label">Today</span>
            {room.schedule.length === 0 ? (
              <p className="t-body ink-secondary">Nothing booked — the room is free all day.</p>
            ) : (
              <div className="table-wrap card">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Meeting</th>
                    </tr>
                  </thead>
                  <tbody>
                    {room.schedule.map((slot) => (
                      <tr key={slot.start_at}>
                        <td className="time">
                          {formatTime(slot.start_at, room.timezone)}–
                          {formatTime(slot.end_at, room.timezone)}
                        </td>
                        <td>
                          {slot.subject}
                          {slot.own && (
                            <span className="pill pill--accent" style={{ marginLeft: 8 }}>
                              <span className="pill__dot" aria-hidden="true" />
                              Yours
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {room.policy?.explanation?.length ? (
            <div className="stack" style={{ gap: "var(--space-2)" }}>
              <span className="t-label">Room rules</span>
              <div className="detail-list">
                {room.policy.explanation.map((item) => (
                  <div className="detail-list__row" key={item.label}>
                    <span className="detail-list__key">{item.label}</span>
                    <span className="detail-list__value t-body ink-secondary">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
